import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Plus, Trash2, Star } from 'lucide-react';
import { brandsAPI } from '../utils/api';
import { toast } from 'sonner';

const Brands = () => {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ name: '', country: '', founded: '', logo: '', featured: false });

  const load = async () => { try { setBrands(await brandsAPI.getAll()); } catch (e) { console.error(e); } finally { setLoading(false); } };
  useEffect(() => { load(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Brand name is required'); return; }
    try {
      await brandsAPI.create({ name: form.name.trim(), country: form.country, founded: form.founded ? parseInt(form.founded) : undefined, logo: form.logo, image: form.logo, featured: form.featured });
      toast.success('Brand created'); setDialogOpen(false); setForm({ name: '', country: '', founded: '', logo: '', featured: false }); load();
    } catch (e) { toast.error(e.message || 'Failed'); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? This will fail if watches reference this brand.`)) return;
    try { await brandsAPI.delete(id); toast.success('Brand deleted'); load(); }
    catch (e) { toast.error(e.message?.includes('referenced') ? `Cannot delete: watches still reference "${name}"` : e.message || 'Delete failed'); }
  };

  if (loading) return <div className="flex items-center justify-center min-h-[60vh]"><div className="text-muted-foreground animate-pulse">Loading...</div></div>;

  return (
    <div className="space-y-6" data-testid="brands-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Brands</h1>
          <p className="text-muted-foreground mt-1 text-sm">{brands.length} brands in catalog</p>
        </div>
        <Button onClick={() => setDialogOpen(true)} className="bg-primary text-primary-foreground hover:bg-[hsl(42,44%,62%)]" data-testid="create-brand-button">
          <Plus className="h-4 w-4 mr-2" /> Add brand
        </Button>
      </div>

      <Card className="overflow-hidden">
        <Table>
          <TableHeader className="bg-muted/50">
            <TableRow>
              <TableHead className="w-14">Logo</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Country</TableHead>
              <TableHead>Founded</TableHead>
              <TableHead className="text-center">Listings</TableHead>
              <TableHead className="text-center">Featured</TableHead>
              <TableHead className="w-14"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {brands.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">No brands yet</TableCell></TableRow>
            ) : brands.map(b => (
              <TableRow key={b.id}>
                <TableCell>{b.logo ? <img src={b.logo} alt={b.name} className="w-10 h-10 rounded object-cover" /> : <div className="w-10 h-10 rounded bg-muted/40 flex items-center justify-center text-xs text-muted-foreground">{b.name?.[0]}</div>}</TableCell>
                <TableCell className="font-medium">{b.name}</TableCell>
                <TableCell className="text-muted-foreground">{b.country || '—'}</TableCell>
                <TableCell className="text-muted-foreground">{b.founded || '—'}</TableCell>
                <TableCell className="text-center"><Badge variant="outline">{b.listingsCount}</Badge></TableCell>
                <TableCell className="text-center">{b.featured ? <Star className="h-4 w-4 text-primary mx-auto fill-primary" /> : <span className="text-muted-foreground">—</span>}</TableCell>
                <TableCell><Button variant="ghost" size="icon" onClick={() => handleDelete(b.id, b.name)} className="text-destructive hover:text-destructive"><Trash2 className="h-4 w-4" /></Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Add Brand</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 pt-2">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Name *</Label>
              <Input placeholder="Patek Philippe" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Country</Label>
                <Input placeholder="Switzerland" value={form.country} onChange={e => setForm({ ...form, country: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Founded</Label>
                <Input type="number" placeholder="1839" value={form.founded} onChange={e => setForm({ ...form, founded: e.target.value })} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Logo</Label>
              <div className="flex gap-2">
                <Input placeholder="https://... or upload" value={form.logo?.startsWith('data:') ? 'Uploaded image' : form.logo} onChange={e => setForm({ ...form, logo: e.target.value })} className="flex-1" />
                <label className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md border border-input bg-background text-xs cursor-pointer hover:bg-accent transition-colors shrink-0">
                  Upload
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    const reader = new FileReader();
                    reader.onload = () => {
                      const img = new Image();
                      img.onload = () => {
                        const canvas = document.createElement('canvas');
                        const MAX = 400;
                        let w = img.width, h = img.height;
                        if (w > MAX || h > MAX) { if (w > h) { h = Math.round(h * MAX / w); w = MAX; } else { w = Math.round(w * MAX / h); h = MAX; } }
                        canvas.width = w; canvas.height = h;
                        canvas.getContext('2d').drawImage(img, 0, 0, w, h);
                        setForm(f => ({ ...f, logo: canvas.toDataURL('image/jpeg', 0.7) }));
                      };
                      img.src = reader.result;
                    };
                    reader.readAsDataURL(file);
                    e.target.value = '';
                  }} />
                </label>
              </div>
              {form.logo && <img src={form.logo} alt="Preview" className="w-full h-24 object-cover rounded mt-1 border border-border" />}
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="featured" checked={form.featured} onChange={e => setForm({ ...form, featured: e.target.checked })} className="rounded" />
              <Label htmlFor="featured" className="text-sm">Featured on storefront</Label>
            </div>
            <div className="flex justify-start gap-2 pt-2">
              <Button type="submit" className="bg-primary text-primary-foreground">Save</Button>
              <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)}>Cancel</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Brands;
